import React, { useState, useEffect, useMemo } from "react";
import Dashboardfilter from "./Dashboardfilter";
import { IoMdArrowDropdown } from "react-icons/io";
import GroomingServiceCard from "./GroomingServicesCard";
import WalkingServiceCard from "./WalkingServicesCard";
import BoardingServiceCard from "./BoardingServicesCard";
import TrainingServiceCard from "./TrainingServicesCard";
import DoctorAppointmentServicesCard from "./DoctorAppointmentServicesCard";
import ShoppingServicesCard from "./ShopingServicesCard";
import "../css/Dashboardservices.css";
import { MdFileDownload } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "react-countup";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../api"; // centralized axios instance

const dropdownOptions = [
  "All Services",
  "Grooming",
  "Walking",
  "Boarding",
  "Training",
  "Doctor Appointment",
  "Shopping",
];

const DashboardServiceList = () => {
  const [selectedOption, setSelectedOption] = useState("Service Select");
  const [showDropdown, setShowDropdown] = useState(false);

  // 🔹 Date filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // 🔹 API data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 🔹 Fetch only service data (independent)
  const fetchServiceData = async (from = "", to = "") => {
    try {
      setLoading(true);
      setError("");

      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;

      // ✅ Fetch full dashboard but extract only `services`
      const response = await api.get("/admin/dashboard/", { params });
      setData(response.data?.services || []);
    } catch (error) {
      console.error("Error fetching service data:", error);
      setError("Unable to load service data.");
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Load on mount
  useEffect(() => {
    fetchServiceData();
  }, []);

  // 📅 Apply filter
  const handleApplyFilter = (from, to) => {
    setFromDate(from);
    setToDate(to);
    fetchServiceData(from, to);
  };

  // 🔄 Transform API services into display format
  const services = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((item) => ({
      name: item.service,
      price: item.price,
      description: item.description || "—",
      date: item.date || "—",
    }));
  }, [data]);

  // ✅ Excel Download Function
  const handleDownloadExcel = () => {
    if (!data || data.length === 0) {
      alert("No data available to download.");
      return;
    }

    if (selectedOption === "All Services" || selectedOption === "Service Select") {
      const excelData = data.map((item, index) => ({
        "S.No": index + 1,
        "Service Name": item.service,
        "Price (₹)": item.price,
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "All Services");
      ws["!cols"] = [{ wch: 6 }, { wch: 25 }, { wch: 15 }];

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(blob, `All_Services_${new Date().toISOString().slice(0, 10)}.xlsx`);
      return;
    }

    const serviceData = data.filter((item) => item.service === selectedOption);
    if (serviceData.length === 0) {
      alert(`No data found for ${selectedOption}.`);
      return;
    }

    const summaryData = Object.entries(serviceData[0]).map(([key, value], index) => ({
      "S.No": index + 1,
      ServicesName: key,
      Value: value,
    }));

    const ws = XLSX.utils.json_to_sheet(summaryData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, selectedOption);
    ws["!cols"] = [{ wch: 30 }, { wch: 30 }, { wch: 25 }];

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, `${selectedOption}_Summary_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <motion.div
      className="Dashboard-service-list-container"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* 🔹 Header */}
      <div className="Dashboard-header">
        <motion.span
          className="Dashboard-title"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          Services
        </motion.span>

        <div className="Dashboard-actions">
          {/* 🔽 Dropdown */}
          <div
            className="Dashboard-dropdown"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            {selectedOption}
            <span className="Dashboard-down">
              <IoMdArrowDropdown />
            </span>
            <AnimatePresence>
              {showDropdown && (
                <motion.ul
                  className="Dashboard-dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {dropdownOptions.map((option, index) => (
                    <motion.li
                      key={index}
                      whileHover={{ backgroundColor: "#f1f1f1" }}
                      onClick={() => {
                        setSelectedOption(option);
                        setShowDropdown(false);
                      }}
                    >
                      {option}
                    </motion.li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          {/* 📅 Filter Button */}
          <motion.button
            className="Dashboard-filter-btn"
            // whileHover={{ scale: 1.05 }}
            // whileTap={{ scale: 0.95 }}
          >
            <Dashboardfilter
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
              handleApplyFilter={handleApplyFilter}
            />
          </motion.button>
        </div>
      </div>

      {/* 🔹 Service Cards Section */}
      <div className="Dashboard-service-cards">
        {loading ? (
          <p className="loading-text">Loading services...</p>
        ) : error ? (
          <p className="error-text">{error}</p>
        ) : selectedOption === "Grooming" ? (
          <GroomingServiceCard data={data} />
        ) : selectedOption === "Walking" ? (
          <WalkingServiceCard data={data} />
        ) : selectedOption === "Boarding" ? (
          <BoardingServiceCard data={data} />
        ) : selectedOption === "Training" ? (
          <TrainingServiceCard data={data} />
        ) : selectedOption === "Doctor Appointment" ? (
          <DoctorAppointmentServicesCard data={data} />
        ) : selectedOption === "Shopping" ? (
          <ShoppingServicesCard data={data} />
        ) : (
          <AnimatePresence>
            {services.map((service, index) => (
              <motion.div
                className="Dashboard-service-card"
                key={index}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.1,
                  ease: "easeOut",
                }}
              >
                <span className="Dashboard-service-name">{service.name}</span>
                <span className="Dashboard-service-amount">
                  ₹
                  <CountUp
                    end={service.price || 0}
                    duration={1.2}
                    separator=","
                  />
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* ✅ Download Button */}
      <motion.button
        className="Dashboard-download-btn"
        onClick={handleDownloadExcel}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        Download All{" "}
        <MdFileDownload className="Dashboard-download-btn-icon" size={20} />
      </motion.button>
    </motion.div>
  );
};

export default DashboardServiceList;
